import type { Akshara } from "@repo/hindi-shared";
import { mulberry32, wordById } from "@repo/hindi-shared";
import { useReducedMotion } from "framer-motion";
import { useEffect, useMemo, useRef, useState } from "react";

import { DualLabel } from "../ui/DualLabel";
import { GlyphTile } from "../ui/GlyphTile";
import { InkButton } from "../ui/InkButton";
import { Shirorekha } from "../ui/Shirorekha";
import { SpeakerIcon } from "../ui/SpeakerIcon";

import type { BuilderTile } from "./builder";
import { buildTarget, checkComposition } from "./builder";
import { gradeFromChoice } from "./grading";
import { dateKey, hashSeed } from "./seed";
import type { ExerciseProps } from "./types";

const DOTTED_CIRCLE = "◌";
const HALANT = "्";
const INK_SETTLE_MS = 550;
const WRONG_FLASH_MS = 600;

/** How many tiles akshara `i` needs, in reading order (matches checkComposition). */
function expectedLength(akshara: Akshara): number {
  let n = akshara.consonantIds.length;
  if (akshara.vowelId !== null) n += 1;
  if (akshara.matraId !== null) n += 1;
  n += akshara.signIds.filter((id) => id !== "sign-halant").length;
  return n;
}

/**
 * Build the live Devanagari string for one akshara's placed tiles: cluster
 * consonants (and an independent vowel, which never co-occurs with
 * consonants in a group) joined by an implicit halant, then the matra/sign
 * glyphs stripped of their dotted-circle placeholder.
 */
function renderGroup(group: BuilderTile[]): string {
  const baseGlyphs = group
    .filter((tile) => tile.kind === "consonant" || tile.kind === "vowel")
    .map((tile) => tile.label);
  const signGlyphs = group
    .filter((tile) => tile.kind === "matra" || tile.kind === "sign")
    .map((tile) => tile.label.replace(DOTTED_CIRCLE, ""));
  return baseGlyphs.join(HALANT) + signGlyphs.join("");
}

/**
 * WordBuilder — the key desktop "writing" exercise: compose a dictated
 * word from letter tiles. Plays the word's audio on mount (replayable);
 * renders one empty slot-group per akshara hanging from a shared
 * shirorekha; a tile tray below holds every needed unit plus distractors.
 * Clicking a tray tile fills the next open sub-slot (in reading order:
 * consonants, then matra/signs) of the first incomplete group; clicking a
 * placed tile returns it to the tray. A live preview above renders the
 * composed word as the learner builds it. "जाँचिए" checks the full
 * composition: correct completes with a grade based on how many checks it
 * took; wrong flashes the mismatched groups and lets the learner keep
 * adjusting. Reduced motion skips the ink-settle delay.
 */
export function WordBuilder({ exercise, audio, onComplete }: ExerciseProps) {
  const word = wordById.get(exercise.itemId);
  const prefersReducedMotion = useReducedMotion();

  const target = useMemo(() => {
    if (word === undefined) return null;
    const rand = mulberry32(hashSeed(exercise.itemId + dateKey()));
    return buildTarget(word, rand);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- word is derived from exercise.itemId
  }, [exercise.itemId]);

  const [placed, setPlaced] = useState<BuilderTile[][]>(
    () => target?.aksharas.map(() => []) ?? [],
  );
  const [attempts, setAttempts] = useState(0);
  const [wrongGroups, setWrongGroups] = useState<Set<number>>(new Set());
  const [settled, setSettled] = useState(false);

  const cancelledRef = useRef(false);
  useEffect(() => {
    cancelledRef.current = false;
    return () => {
      cancelledRef.current = true;
    };
  }, []);

  useEffect(() => {
    if (word !== undefined) {
      void audio.speak(word.devanagari);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- speak once per mount
  }, []);

  if (word === undefined || target === null) return null;

  const placedTileIds = new Set(placed.flat().map((tile) => tile.id));
  const trayTiles = target.tiles.filter((tile) => !placedTileIds.has(tile.id));

  function handleReplay(): void {
    void audio.speak(word!.devanagari);
  }

  function handleTrayClick(tile: BuilderTile): void {
    if (settled) return;
    setWrongGroups(new Set());
    setPlaced((prev) => {
      const next = prev.map((group) => [...group]);
      const groupIndex = next.findIndex(
        (group, i) => group.length < expectedLength(target!.aksharas[i]!),
      );
      if (groupIndex === -1) return prev; // every group already full
      next[groupIndex] = [...next[groupIndex]!, tile];
      return next;
    });
  }

  function handlePlacedClick(groupIndex: number, tileIndex: number): void {
    if (settled) return;
    setWrongGroups(new Set());
    setPlaced((prev) => {
      const next = prev.map((group) => [...group]);
      next[groupIndex]!.splice(tileIndex, 1);
      return next;
    });
  }

  function handleCheck(): void {
    if (settled) return;
    const nextAttempts = attempts + 1;
    setAttempts(nextAttempts);

    if (checkComposition(target!.aksharas, placed)) {
      setSettled(true);
      const grade = gradeFromChoice(nextAttempts);
      const delay = prefersReducedMotion ? 0 : INK_SETTLE_MS;
      window.setTimeout(() => {
        if (cancelledRef.current) return;
        onComplete({ grade, itemId: exercise.itemId });
      }, delay);
      return;
    }

    // Flag every group that doesn't yet match its expected content so the
    // wrong-flash can highlight exactly what needs fixing.
    const mismatched = new Set<number>();
    target!.aksharas.forEach((akshara, i) => {
      const ok = checkComposition([akshara], [placed[i] ?? []]);
      if (!ok) mismatched.add(i);
    });
    setWrongGroups(mismatched);
    window.setTimeout(() => {
      if (cancelledRef.current) return;
      setWrongGroups(new Set());
    }, WRONG_FLASH_MS);
  }

  const allGroupsFull = target.aksharas.every(
    (akshara, i) => (placed[i]?.length ?? 0) === expectedLength(akshara),
  );
  const composedPreview = placed.map(renderGroup).join("");

  return (
    <div className="flex flex-col items-center gap-8 text-center">
      <p className="font-dev text-sm text-ink-soft uppercase">
        <DualLabel variant="inline" en="Write it" hi="लिखिए" />
      </p>

      <InkButton variant="ghost" onClick={handleReplay}>
        <SpeakerIcon className="mr-2" />
        <DualLabel variant="inline" en="Replay" hi="फिर सुनो" />
      </InkButton>

      {/* Live composed-word preview. */}
      <p
        lang="hi"
        className="font-dev min-h-[1.2em] text-5xl leading-none text-ink"
        aria-live="polite"
      >
        {composedPreview}
      </p>

      {/* Akshara slot groups, hanging from a shared shirorekha. */}
      <Shirorekha>
        <div className="flex flex-wrap justify-center gap-4 pb-1">
          {target.aksharas.map((akshara, groupIndex) => {
            const group = placed[groupIndex] ?? [];
            const slots = expectedLength(akshara);
            const isWrong =
              wrongGroups.has(groupIndex) && group.length === slots;
            return (
              <div key={groupIndex} className="flex gap-1">
                {Array.from({ length: slots }).map((_, slotIndex) => {
                  const tile = group[slotIndex];
                  if (tile === undefined) {
                    return (
                      <div
                        key={slotIndex}
                        className="flex h-16 w-16 items-center justify-center border border-dashed border-rule bg-paper text-ink-soft"
                      />
                    );
                  }
                  return (
                    <GlyphTile
                      key={tile.id}
                      glyph={tile.label}
                      size="md"
                      state={isWrong ? "wrong" : "selected"}
                      onClick={() => handlePlacedClick(groupIndex, slotIndex)}
                    />
                  );
                })}
              </div>
            );
          })}
        </div>
      </Shirorekha>

      {/* Tile tray. */}
      <div className="grid grid-cols-4 gap-3 sm:grid-cols-6">
        {trayTiles.map((tile) => (
          <GlyphTile
            key={tile.id}
            glyph={tile.label}
            size="md"
            disabled={settled}
            onClick={() => handleTrayClick(tile)}
          />
        ))}
      </div>

      <InkButton
        variant="sindoor"
        disabled={!allGroupsFull || settled}
        onClick={handleCheck}
      >
        <DualLabel variant="inline" en="Check" hi="जाँचिए" />
      </InkButton>
    </div>
  );
}
